import React, { useEffect, useState } from "react";
import { Box, Typography, Button, Link } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import EditNoteOutlinedIcon from "@mui/icons-material/EditNoteOutlined";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import ContactUs from "./ContactUs";

const quickSteps = [
  {
    title: "Write",
    icon: <EditNoteOutlinedIcon sx={{ fontSize: 18 }} />,
  },
  {
    title: "Refine",
    icon: <AutoAwesomeOutlinedIcon sx={{ fontSize: 18 }} />,
  },
  {
    title: "Explore",
    icon: <SearchOutlinedIcon sx={{ fontSize: 18 }} />,
  },
];

const typingSentences = [
  "Find context fast before meetings.",
  "Offload the mental clutter.",
  "See connections others miss.",
];

const LandingPage = () => {
  const [sentenceIndex, setSentenceIndex] = useState(0);
  const [visibleChars, setVisibleChars] = useState(0);
  const [isFadingOut, setIsFadingOut] = useState(false);
  const currentSentence = typingSentences[sentenceIndex];

  useEffect(() => {
    const atFullSentence = visibleChars >= currentSentence.length;
    let delay = 36;
    if (atFullSentence && !isFadingOut) delay = 1300;
    if (isFadingOut) delay = 260;

    const timer = setTimeout(() => {
      if (isFadingOut) {
        setIsFadingOut(false);
        setVisibleChars(0);
        setSentenceIndex((prev) => (prev + 1) % typingSentences.length);
        return;
      }

      if (atFullSentence) {
        setIsFadingOut(true);
        return;
      }

      setVisibleChars((prev) => prev + 1);
    }, delay);

    return () => clearTimeout(timer);
  }, [currentSentence.length, isFadingOut, visibleChars]);

  return (
    <Box
      id="nr-marketing-landing"
      sx={{
        minHeight: "100vh",
        width: "100%",
        boxSizing: "border-box",
        overflowX: "hidden",
        color: "#2b2140",
        fontFamily: "Roboto, Arial, sans-serif",
        background:
          "radial-gradient(140% 120% at 8% 0%, rgba(142, 109, 210, 0.2) 0%, rgba(142, 109, 210, 0) 52%), radial-gradient(110% 90% at 100% 0%, rgba(73, 32, 153, 0.18) 0%, rgba(73, 32, 153, 0) 48%), linear-gradient(180deg, #f7f4fb 0%, #ffffff 60%, #f3eefb 100%)",
      }}
    >
      <style>
        {`
          #nr-marketing-landing .nr-fade-up {
            animation: nrFadeUp 560ms ease both;
          }
          #nr-marketing-landing .nr-fade-up-delay-1 { animation-delay: 90ms; }
          #nr-marketing-landing .nr-fade-up-delay-2 { animation-delay: 180ms; }
          #nr-marketing-landing .nr-fade-up-delay-3 { animation-delay: 270ms; }
          #nr-marketing-landing .nr-float {
            animation: nrFloat 4.8s ease-in-out infinite;
          }
          #nr-marketing-landing .nr-pulse {
            animation: nrPulse 2.1s ease-in-out infinite;
          }
          #nr-marketing-landing .nr-shimmer {
            position: relative;
            overflow: hidden;
          }
          #nr-marketing-landing .nr-shimmer::after {
            content: "";
            position: absolute;
            inset: -20% auto -20% -55%;
            width: 62%;
            transform: skewX(-20deg);
            background: linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.45) 50%, rgba(255,255,255,0) 100%);
            animation: nrSweep 2.6s ease-in-out infinite;
          }
          @keyframes nrFadeUp {
            from { opacity: 0; transform: translateY(12px); }
            to { opacity: 1; transform: translateY(0); }
          }
          @keyframes nrFloat {
            0% { transform: translateY(0); }
            50% { transform: translateY(-10px); }
            100% { transform: translateY(0); }
          }
          @keyframes nrPulse {
            0%, 100% { box-shadow: 0 0 0 0 rgba(93, 50, 186, 0.25); }
            50% { box-shadow: 0 0 0 12px rgba(93, 50, 186, 0); }
          }
          @keyframes nrSweep {
            0% { left: -55%; }
            100% { left: 125%; }
          }
          @keyframes nrCaretBlink {
            0%, 44% { opacity: 1; }
            45%, 100% { opacity: 0; }
          }
          #nr-marketing-landing .nr-typing-caret {
            animation: nrCaretBlink 1s step-end infinite;
          }
          @media (prefers-reduced-motion: reduce) {
            #nr-marketing-landing .nr-fade-up,
            #nr-marketing-landing .nr-float,
            #nr-marketing-landing .nr-pulse,
            #nr-marketing-landing .nr-shimmer::after,
            #nr-marketing-landing .nr-typing-caret {
              animation: none !important;
            }
          }
        `}
      </style>

      <Box
        sx={{
          maxWidth: 1120,
          mx: "auto",
          px: { xs: 2, sm: 3 },
          pt: { xs: 2, sm: 2.5 },
          pb: "calc(env(safe-area-inset-bottom) + 28px)",
        }}
      >
        <Box
          className="nr-fade-up"
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: { xs: 2.5, md: 3.5 },
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", color: "#492099", fontWeight: 700, fontSize: { xs: 20, sm: 22 }, marginLeft: "-4px" }}>
            <img src="/logo2-removebg-preview.png" alt="NoteRefinery logo" style={{ width: 42, height: 42, marginRight: 0 }} />
            <span style={{ marginTop: 6 }}>NoteRefinery</span>
          </Box>
          <Button
            variant="outlined"
            component={RouterLink}
            to="/signin"
            sx={{
              borderRadius: 99,
              textTransform: "none",
              borderColor: "rgba(73, 32, 153, 0.32)",
              color: "#492099",
              fontWeight: 700,
              px: 2,
            }}
          >
            Log in
          </Button>
        </Box>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", lg: "1.04fr 0.96fr" },
            gap: { xs: 2.5, lg: 3 },
            alignItems: "stretch",
          }}
        >
          <Box className="nr-fade-up nr-fade-up-delay-1" sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Box sx={{ display: "inline-flex", alignItems: "center", width: "fit-content", gap: 1, px: 1.2, py: 0.6, borderRadius: 99, border: "1px solid rgba(73, 32, 153, 0.2)", color: "#492099", fontSize: 12, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", backgroundColor: "rgba(255, 255, 255, 0.72)" }}>
              <AutoAwesomeOutlinedIcon sx={{ fontSize: 15 }} />
              From notes to knowledge
            </Box>

            <Typography sx={{ fontSize: { xs: 33, sm: 44 }, lineHeight: 1.06, fontWeight: 800, letterSpacing: "-0.02em", maxWidth: 620 }}>
              Clear thinking, not manual linking.
            </Typography>

            <Typography
              sx={{
                color: "#574575",
                fontSize: { xs: 18, sm: 22 },
                lineHeight: 1.35,
                fontWeight: 700,
                maxWidth: 720,
                minHeight: { xs: 72, sm: 88 },
                opacity: isFadingOut ? 0 : 1,
                transition: "opacity 220ms ease",
              }}
            >
              {currentSentence.slice(0, visibleChars)}
              <Box component="span" className="nr-typing-caret" sx={{ display: "inline-block", ml: 0.25, fontWeight: 500 }}>
                |
              </Box>
            </Typography>

            <Box sx={{ display: "flex", gap: 1.2, flexDirection: { xs: "column", sm: "row" }, pt: 0.4 }}>
              <Button
                variant="contained"
                component={RouterLink}
                to="/register"
                className="nr-shimmer"
                sx={{
                  borderRadius: 99,
                  py: 1.2,
                  px: 2.8,
                  minWidth: 190,
                  textTransform: "none",
                  backgroundColor: "#492099",
                  fontWeight: 800,
                  fontSize: 16,
                  boxShadow: "0 12px 24px rgba(73, 32, 153, 0.26)",
                  ":hover": {
                    backgroundColor: "#3f1a85",
                    boxShadow: "0 12px 24px rgba(73, 32, 153, 0.26)",
                  },
                }}
              >
                Create account
              </Button>
              <Button
                variant="text"
                component={RouterLink}
                to="/signin"
                sx={{
                  borderRadius: 99,
                  py: 1.2,
                  px: 2.2,
                  minWidth: 130,
                  textTransform: "none",
                  color: "#492099",
                  fontWeight: 700,
                  fontSize: 15,
                  ":hover": { backgroundColor: "rgba(73, 32, 153, 0.06)" },
                }}
              >
                Open app
              </Button>
            </Box>

            <Box
              className="nr-fade-up nr-fade-up-delay-2"
              sx={{
                position: "relative",
                mt: 0.5,
                px: 0.4,
                pt: 0.6,
                pb: 0.2,
              }}
            >
              <Box
                sx={{
                  position: "absolute",
                  left: { xs: 24, sm: 40 },
                  right: { xs: 24, sm: 40 },
                  top: 22,
                  height: 2,
                  borderRadius: 99,
                  background:
                    "linear-gradient(90deg, rgba(73, 32, 153, 0.3) 0%, rgba(73, 32, 153, 0.55) 52%, rgba(73, 32, 153, 0.3) 100%)",
                }}
              />
              <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 0.8 }}>
                {quickSteps.map((step, index) => (
                  <Box key={step.title} sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.7 }}>
                    <Box
                      sx={{
                        width: 44,
                        height: 44,
                        borderRadius: "50%",
                        border: "1px solid rgba(73, 32, 153, 0.2)",
                        backgroundColor: index === 1 ? "#492099" : "rgba(255,255,255,0.96)",
                        color: index === 1 ? "#fff" : "#492099",
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        zIndex: 1,
                        boxShadow: index === 1 ? "0 8px 18px rgba(73, 32, 153, 0.24)" : "none",
                      }}
                    >
                      {step.icon}
                    </Box>
                    <Typography sx={{ color: "#3a2c56", fontSize: 13, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em" }}>
                      {step.title}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>

          <Box className="nr-fade-up nr-fade-up-delay-2" sx={{ position: "relative", minHeight: { xs: 350, sm: 430 }, display: "flex", alignItems: "stretch" }}>
            <Box
              className="nr-float"
              sx={{
                position: "absolute",
                width: 180,
                height: 180,
                borderRadius: "50%",
                background: "radial-gradient(circle, rgba(73, 32, 153, 0.22) 0%, rgba(73, 32, 153, 0) 72%)",
                right: -24,
                top: -26,
                pointerEvents: "none",
              }}
            />
            <Box
              sx={{
                position: "absolute",
                width: 220,
                height: 220,
                borderRadius: "50%",
                background: "radial-gradient(circle, rgba(140, 96, 230, 0.18) 0%, rgba(140, 96, 230, 0) 72%)",
                left: -70,
                bottom: -78,
                pointerEvents: "none",
              }}
            />

            <Box
              sx={{
                position: "relative",
                zIndex: 1,
                borderRadius: 3,
                border: "1px solid rgba(73, 32, 153, 0.18)",
                background: "linear-gradient(180deg, rgba(255,255,255,0.95) 0%, rgba(250,247,255,0.95) 100%)",
                boxShadow: "0 22px 50px rgba(73, 32, 153, 0.16)",
                p: 1.5,
                display: "flex",
                flexDirection: "column",
                width: "100%",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 0.8, pb: 1 }}>
                <Typography sx={{ fontSize: 13, color: "#5c4a7d", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em" }}>
                  Live workspace preview
                </Typography>
                <Box className="nr-pulse" sx={{ width: 10, height: 10, borderRadius: "50%", backgroundColor: "#492099" }} />
              </Box>

              <Box sx={{ borderRadius: 2, border: "1px solid rgba(73, 32, 153, 0.12)", backgroundColor: "#fff", px: 1.2, py: 1, display: "grid", gridTemplateColumns: "1fr auto", gap: 1, mb: 1.2 }}>
                <Typography sx={{ color: "#3d3158", fontSize: 14, fontWeight: 700 }}>Mobile redesign sync</Typography>
                <Typography sx={{ color: "#7a6b98", fontSize: 12 }}>2m ago</Typography>
                <Typography sx={{ gridColumn: "1 / span 2", color: "#67567f", fontSize: 13 }}>
                  Refine surfaced owner, reviewer, due date, and blocker for the release.
                </Typography>
                <Box sx={{ gridColumn: "1 / span 2", mt: 0.2, display: "flex", flexDirection: "column", gap: 0.35 }}>
                  <Typography sx={{ color: "#564574", fontSize: 12.3 }}>
                    - Maria to finalize hero and CTA copy by Thursday.
                  </Typography>
                  <Typography sx={{ color: "#564574", fontSize: 12.3 }}>
                    - Liam to review nav behavior on iPhone 12 and iPhone 14.
                  </Typography>
                  <Typography sx={{ color: "#564574", fontSize: 12.3 }}>
                    - API delay still affects live concept chips on first load.
                  </Typography>
                  <Typography sx={{ color: "#564574", fontSize: 12.3 }}>
                    - Next check-in: Friday, 10:00 AM.
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ borderRadius: 2, border: "1px solid rgba(73, 32, 153, 0.12)", backgroundColor: "#fff", p: 1.1, mb: 1.2 }}>
                <Typography sx={{ color: "#4f3f6d", fontWeight: 700, fontSize: 12, letterSpacing: "0.08em", textTransform: "uppercase", mb: 0.9 }}>
                  Connected concepts
                </Typography>
                <Box sx={{ display: "flex", gap: 0.8, flexWrap: "wrap" }}>
                  {["Mobile Redesign", "Maria", "Liam", "May 15", "API Delay"].map((chip) => (
                    <Box key={chip} sx={{ borderRadius: 99, px: 1.2, py: 0.5, border: "1px solid rgba(73, 32, 153, 0.16)", backgroundColor: "rgba(73, 32, 153, 0.06)", color: "#492099", fontSize: 12, fontWeight: 700 }}>
                      {chip}
                    </Box>
                  ))}
                </Box>
              </Box>

              <Box sx={{ flex: 1, minHeight: 170, borderRadius: 2, border: "1px solid rgba(73, 32, 153, 0.12)", background: "linear-gradient(180deg, #f8f4ff 0%, #ffffff 80%)", p: 1.1 }}>
                <svg viewBox="0 0 360 180" aria-hidden="true" style={{ width: "100%", height: "100%" }}>
                  <g stroke="rgba(122, 107, 152, 0.42)" strokeWidth="2.2" fill="none">
                    <line x1="180" y1="90" x2="92" y2="44" />
                    <line x1="180" y1="90" x2="276" y2="42" />
                    <line x1="180" y1="90" x2="74" y2="140" />
                    <line x1="180" y1="90" x2="290" y2="140" />
                    <line x1="92" y1="44" x2="276" y2="42" />
                    <line x1="276" y1="42" x2="290" y2="140" />
                    <line x1="290" y1="140" x2="74" y2="140" />
                    <line x1="74" y1="140" x2="92" y2="44" />
                  </g>

                  <g fill="#7b6b99" fontSize="8.5" fontWeight="700">
                    <text x="128" y="62" textAnchor="middle">owned by</text>
                    <text x="232" y="61" textAnchor="middle">reviewed by</text>
                    <text x="122" y="118" textAnchor="middle">due</text>
                    <text x="236" y="118" textAnchor="middle">blocked by</text>
                  </g>

                  <circle cx="180" cy="90" r="32" fill="#492099" />
                  <circle cx="92" cy="44" r="20" fill="#e7dff7" stroke="rgba(73, 32, 153, 0.18)" />
                  <circle cx="276" cy="42" r="22" fill="#e7dff7" stroke="rgba(73, 32, 153, 0.18)" />
                  <circle cx="74" cy="140" r="21" fill="#e7dff7" stroke="rgba(73, 32, 153, 0.18)" />
                  <circle cx="290" cy="140" r="21" fill="#e7dff7" stroke="rgba(73, 32, 153, 0.18)" />

                  <text x="180" y="86" textAnchor="middle" fill="#ffffff" fontSize="11" fontWeight="700">Mobile</text>
                  <text x="180" y="100" textAnchor="middle" fill="#ffffff" fontSize="11" fontWeight="700">Redesign</text>
                  <text x="92" y="48" textAnchor="middle" fill="#5a4a7b" fontSize="10.5" fontWeight="700">Maria</text>
                  <text x="276" y="46" textAnchor="middle" fill="#5a4a7b" fontSize="10.5" fontWeight="700">Liam</text>
                  <text x="74" y="144" textAnchor="middle" fill="#5a4a7b" fontSize="10.5" fontWeight="700">May 15</text>
                  <text x="290" y="144" textAnchor="middle" fill="#5a4a7b" fontSize="9.8" fontWeight="700">API Delay</text>
                </svg>
              </Box>
            </Box>
          </Box>
        </Box>

        <Box sx={{ mt: 2.6, display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center" }}>
          <ContactUs />
          <Typography variant="body2" sx={{ color: "#492099", mt: 0.8 }}>
            <Link component={RouterLink} to="/privacy_policy" sx={{ color: "#492099", textDecoration: "underline", fontWeight: 500 }}>
              Privacy Policy
            </Link>
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default LandingPage;
